/**
 * <product-card> — colour thumbnails on a product card.
 *
 * Hovering a thumbnail previews its image in the card's main media; leaving
 * restores the active thumbnail (or the product default). Clicking a thumbnail
 * makes it active and repoints the card link to that colour's variant.
 *
 * Registered as a custom element so each card manages its own listeners.
 * Cards injected by AJAX facet filtering upgrade automatically.
 *
 * The whole module is guarded: the snippet ships this <script> per card, so a grid
 * loads the same src many times. Without the guard the second run would redeclare
 * the top-level const and throw, aborting that execution.
 */
if (!customElements.get('product-card')) {
  // Pointer travel (px) beyond which a press is treated as a drag, not a click.
  const DRAG_THRESHOLD = 5;

  class ProductCard extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('.card-product');
    this.mainImage = this.querySelector('.card-product__image--primary');
    this.track = this.querySelector('.card-product__thumbs');
    this.thumbs = Array.from(this.querySelectorAll('.card-product__thumb'));

    if (!this.mainImage || this.thumbs.length === 0) return;

    // The main image ships with a srcset; the browser would pick from it and
    // ignore any src we set. Remember the default pair so we can restore it.
    this.defaultSrc = this.mainImage.dataset.defaultSrc || this.mainImage.src;
    this.defaultSrcset = this.mainImage.getAttribute('srcset') || '';

    this.dragged = false;
    this.dragging = false;

    this.onThumbEnter = this.onThumbEnter.bind(this);
    this.onThumbLeave = this.onThumbLeave.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragMove = this.onDragMove.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onTrackClickCapture = this.onTrackClickCapture.bind(this);
    this.onDragStartNative = (event) => event.preventDefault();

    for (const thumb of this.thumbs) {
      thumb.addEventListener('mouseenter', this.onThumbEnter);
      thumb.addEventListener('mouseleave', this.onThumbLeave);
    }

    this.track.addEventListener('pointerdown', this.onDragStart);
    this.track.addEventListener('pointermove', this.onDragMove);
    this.track.addEventListener('pointerup', this.onDragEnd);
    this.track.addEventListener('pointercancel', this.onDragEnd);
    // pointer capture makes clicks target the track, not the thumb, so we never
    // rely on the thumb's own click — instead we always swallow the click here
    // (stops it reaching the card link) and select the colour on pointerup.
    this.track.addEventListener('click', this.onTrackClickCapture, true);
    // Kill the browser's native image/link drag ("ghost" drag). The dragstart
    // fires on the <a> ancestor, so listen on the card, not just the track.
    this.addEventListener('dragstart', this.onDragStartNative);
  }

  disconnectedCallback() {
    for (const thumb of this.thumbs || []) {
      thumb.removeEventListener('mouseenter', this.onThumbEnter);
      thumb.removeEventListener('mouseleave', this.onThumbLeave);
    }
    this.removeEventListener('dragstart', this.onDragStartNative);
    if (!this.track) return;
    this.track.removeEventListener('pointerdown', this.onDragStart);
    this.track.removeEventListener('pointermove', this.onDragMove);
    this.track.removeEventListener('pointerup', this.onDragEnd);
    this.track.removeEventListener('pointercancel', this.onDragEnd);
    this.track.removeEventListener('click', this.onTrackClickCapture, true);
  }

  onDragStart(event) {
    if (event.button !== 0) return;
    this.dragging = true;
    this.dragged = false;
    this.dragStartX = event.clientX;
    this.dragStartScroll = this.track.scrollLeft;
    // Route every subsequent pointer event to the track, so the pointer never
    // "grabs" a nested image or link while scrolling.
    this.track.setPointerCapture(event.pointerId);
  }

  onDragMove(event) {
    if (!this.dragging) return;
    const delta = event.clientX - this.dragStartX;
    if (!this.dragged && Math.abs(delta) > DRAG_THRESHOLD) {
      this.dragged = true;
      this.track.classList.add('is-dragging');
    }
    if (this.dragged) {
      this.classList.add('is-scrolling');
      this.track.scrollLeft = this.dragStartScroll - delta;
    }
  }

  onDragEnd(event) {
    if (!this.dragging) return;
    this.dragging = false;
    this.track.classList.remove('is-dragging');
    this.classList.remove('is-scrolling');
    if (event.pointerId != null && this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }

    // A press that didn't travel is a colour selection, not a scroll.
    if (!this.dragged && event.type === 'pointerup') {
      const thumb = document.elementFromPoint(event.clientX, event.clientY)?.closest('.card-product__thumb');
      if (thumb && this.contains(thumb)) this.selectThumb(thumb);
    }
  }

  onTrackClickCapture(event) {
    // Pointer capture routes the click to the track; always stop it here so it
    // never reaches the card link. Colour selection already happened on pointerup.
    event.preventDefault();
    event.stopPropagation();
    this.dragged = false;
  }

  setMainImage(src) {
    if (!src) return;
    // Drop srcset so the browser honours the src we just set.
    this.mainImage.removeAttribute('srcset');
    this.mainImage.src = src;
  }

  restoreDefault() {
    this.mainImage.src = this.defaultSrc;
    if (this.defaultSrcset) {
      this.mainImage.setAttribute('srcset', this.defaultSrcset);
    }
  }

  restoreActive() {
    const active = this.querySelector('.card-product__thumb--active');
    if (active) {
      this.setMainImage(active.dataset.image);
    } else {
      this.restoreDefault();
    }
  }

  onThumbEnter(event) {
    this.setMainImage(event.currentTarget.dataset.image);
  }

  onThumbLeave() {
    this.restoreActive();
  }

  selectThumb(thumb) {
    const previous = this.querySelector('.card-product__thumb--active');
    if (previous) previous.classList.remove('card-product__thumb--active');
    thumb.classList.add('card-product__thumb--active');

    this.setMainImage(thumb.dataset.image);

    if (thumb.dataset.variantUrl && this.link) {
      this.link.setAttribute('href', thumb.dataset.variantUrl);
    }
  }
  }

  customElements.define('product-card', ProductCard);
}
